import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import type { Percentage} from "@@/shared/percentage";
import { percentageZodSchema } from "@@/shared/percentage";
import { placementZodSchema } from "@@/shared/relative-placement";
import { storageItemImageIdZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-id";
import { storageItemImageReferenceZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-reference";
import type { HexColor} from "@@/shared/stylable/hex-color/hex-color";
import { hexColor } from "@@/shared/stylable/hex-color/hex-color";
import type {
    Translatable} from "@@/shared/translations/translation";
import {
    asTranslatableValue,
    translatableZodSchema,
    translation,
} from "@@/shared/translations/translation";
import { setWithinPercentageRange } from "@@/utilities/set-within-range";
import { faRectanglePortrait } from "@fortawesome/pro-solid-svg-icons";
import type { Except } from "type-fest";
import { z } from "zod";
import { alignmentZodSchema } from "../../alignments-and-placements";
import { pageBlockBaseZodSchema } from "../page-block-base";
import { pageBlockIdFactory } from "../page-block-id";
import type {
    PageBlockTitleSize} from "../shared/page-block-title-size";
import {
    pageBlockTitleSizeZodSchema,
} from "../shared/page-block-title-size";

const posterPageBlockZodSchema = pageBlockBaseZodSchema.merge(
    z.object({
        title: translatableZodSchema,
        subtitle: translatableZodSchema,
        alignment: alignmentZodSchema,
        subtitlePlacement: placementZodSchema,
        type: z.literal("POSTER_PAGE_BLOCK"),
        titleSize: pageBlockTitleSizeZodSchema,
        textShadow: z.boolean(),
        posterImage: z
            .union([
                storageItemImageReferenceZodSchema,
                storageItemImageIdZodSchema,
            ])
            .optional(),
        height: percentageZodSchema,
    }),
);

type PosterPageBlock = z.infer<typeof posterPageBlockZodSchema>;

const posterPageBlockIcon = faRectanglePortrait;
type PosterPageBlockParams = Except<
    PosterPageBlock,
    | "_id"
    | "type"
    | "_type"
    | "_createdAt"
    | "title"
    | "subtitle"
    | "titleSize"
    | "textShadow"
    | "height"
> & {
    title?: Translatable;
    subtitle?: Translatable;
    textShadow?: boolean;
    titleSize?: PageBlockTitleSize;
    height?: Percentage;
};
const posterPageBlockFactory = (
    params: PosterPageBlockParams,
): PosterPageBlock => {
    const horizontal = params.alignment?.horizontal ?? "left";
    const vertical = params.alignment?.vertical ?? "top";
    return {
        alignment: {
            horizontal,
            vertical,
        },
        subtitlePlacement: params.subtitlePlacement ?? "below",
        textColor: params.textColor ?? ("#ffffff" as HexColor),
        backgroundColor: params.backgroundColor ?? hexColor("#ffffff"),
        subtitle: asTranslatableValue(params.subtitle),
        title: asTranslatableValue(params.title),
        pageId: params.pageId,
        posterImage: params.posterImage,
        _id: pageBlockIdFactory(),
        _type: "PAGE_BLOCK",
        _createdAt: nowIsoAndUnix(),
        type: "POSTER_PAGE_BLOCK",
        accentColor: params.accentColor,
        height: params.height
            ? setWithinPercentageRange(params.height)
            : (0.45 as Percentage),
        titleSize: params.titleSize ?? "M",
        textShadow: params.textShadow ?? true,
    };
};

const PosterPageBlock_Edit = () => {
    return null;
};

const posterPageBlockType = "POSTER_PAGE_BLOCK";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isPosterPageBlock = (input: any): input is PosterPageBlock => {
    return input._type === "PAGE_BLOCK" && input.type === "POSTER_PAGE_BLOCK";
};

const descriptions: Record<string, Translatable> = {
    height: translation({
        sv: "Höjden på posterblocket i procent av sidans höjd",
        en: "The height of the poster block in percentage of the page height.",
    }),
};

export {
    PosterPageBlock_Edit,
    isPosterPageBlock,
    descriptions as posterPageBlockDescriptions,
    posterPageBlockFactory,
    posterPageBlockIcon,
    posterPageBlockType,
    posterPageBlockZodSchema,
};
export type { PosterPageBlock };
